import { useMemo } from 'react';
import { useFormField } from '@frontend/design-system';
import { AxisGroup, AxisOption } from './types';

// ── Pure helpers ─────────────────────────────────────────────────────

const getChildValues = (option: AxisOption): string[] => {
  if (!option.children) return [];
  return option.children.reduce<string[]>((acc, child) => {
    acc.push(child.value);
    acc.push(...getChildValues(child));
    return acc;
  }, []);
};

const getLeafValues = (option: AxisOption): string[] => {
  if (option.children && option.children.length > 0) {
    return option.children.flatMap(getLeafValues);
  }
  return [option.value];
};

const getAllLeafValues = (options: AxisOption[]): string[] => options.flatMap(getLeafValues);

const matchesSearch = (option: AxisOption, query: string): boolean => {
  const searchText = (option.searchValue ?? option.label).toLowerCase();
  return searchText.includes(query);
};

const filterOptions = (options: AxisOption[], query: string): AxisOption[] => {
  if (!query) return options;

  return options.reduce<AxisOption[]>((acc, option) => {
    if (matchesSearch(option, query)) {
      acc.push(option);
      return acc;
    }

    if (option.children) {
      const filteredChildren = option.children.filter((child) => matchesSearch(child, query));
      if (filteredChildren.length > 0) {
        acc.push({ ...option, children: filteredChildren });
      }
    }

    return acc;
  }, []);
};

// ── Hook ─────────────────────────────────────────────────────────────

export interface UseCheckboxGroupStateProps {
  group: AxisGroup;
  draftY: string[];
  onCheckboxChange: (values: string[]) => void;
}

export interface OptionState {
  checked: boolean;
  indeterminate: boolean;
}

export function useCheckboxGroupState({ group, draftY, onCheckboxChange }: UseCheckboxGroupStateProps) {
  const searchField = useFormField({ type: 'text' });
  const query = ((searchField.value as string) || '').toLowerCase().trim();

  const filteredOptions = useMemo(() => {
    if (!group.searchable || !query) return group.options;
    return filterOptions(group.options, query);
  }, [group.options, group.searchable, query]);

  const allLeafValues = useMemo(() => getAllLeafValues(group.options), [group.options]);
  const allLeafValuesSet = useMemo(() => new Set(allLeafValues), [allLeafValues]);
  const filteredLeafValues = useMemo(() => getAllLeafValues(filteredOptions), [filteredOptions]);
  const filteredLeafValuesSet = useMemo(() => new Set(filteredLeafValues), [filteredLeafValues]);
  const draftYSet = useMemo(() => new Set(draftY), [draftY]);
  const selectedInGroup = useMemo(() => draftY.filter((v) => allLeafValuesSet.has(v)), [draftY, allLeafValuesSet]);

  const handleSelectAll = () => {
    const valuesToAdd = filteredLeafValues.filter((v) => !draftYSet.has(v));
    onCheckboxChange([...draftY, ...valuesToAdd]);
  };

  const handleClear = () => {
    onCheckboxChange(draftY.filter((v) => !filteredLeafValuesSet.has(v)));
  };

  const handleOptionChange = (option: AxisOption, valOrEvent: any) => {
    const isChecked = typeof valOrEvent === 'boolean' ? valOrEvent : !!valOrEvent?.target?.checked;
    let newDraftY = [...draftY];

    if (option.children && option.children.length > 0) {
      const childValues = getChildValues(option);
      if (isChecked) {
        const existingSet = new Set(newDraftY);
        childValues.forEach((v) => {
          if (!existingSet.has(v)) {
            newDraftY.push(v);
            existingSet.add(v);
          }
        });
      } else {
        const childSet = new Set(childValues);
        newDraftY = newDraftY.filter((v) => !childSet.has(v));
      }
    } else {
      if (isChecked) {
        if (!draftYSet.has(option.value)) {
          newDraftY.push(option.value);
        }
      } else {
        newDraftY = newDraftY.filter((v) => v !== option.value);
      }
    }

    onCheckboxChange(newDraftY);
  };

  const getOptionState = (option: AxisOption): OptionState => {
    const hasChildren = option.children && option.children.length > 0;

    if (hasChildren) {
      const childValues = getChildValues(option);
      const selectedChildrenCount = childValues.filter((v) => draftYSet.has(v)).length;
      return {
        checked: selectedChildrenCount === childValues.length && childValues.length > 0,
        indeterminate: selectedChildrenCount > 0 && selectedChildrenCount < childValues.length,
      };
    }

    return {
      checked: draftYSet.has(option.value),
      indeterminate: false,
    };
  };

  return {
    searchField,
    filteredOptions,
    filteredLeafValues,
    selectedInGroup,
    handleSelectAll,
    handleClear,
    handleOptionChange,
    getOptionState,
  };
}
